import { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { verifyOTP, resendOTP } from "../../apis/api/OTPApi";
import "./OTPModal.css";

const OTPModal = ({ isOpen, onClose, email, onVerified }) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [canResend, setCanResend] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const inputRefs = [useRef(), useRef(), useRef(), useRef()];

  // Countdown timer for OTP expiration (10 minutes)
  useEffect(() => {
    if (!isOpen) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error("OTP expired. Please request a new one.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  // Resend button timer (60 seconds)
  useEffect(() => {
    if (!isOpen) return;
    
    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp(["", "", "", ""]);
      setTimeLeft(600);
      setCanResend(false);
      setResendTimer(60);
      setTimeout(() => inputRefs[0].current?.focus(), 100);
    }
  }, [isOpen]);

  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Take only last character
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
    
    // Handle paste
    if (e.key === "v" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      navigator.clipboard.readText().then((text) => {
        const digits = text.replace(/\D/g, "").slice(0, 4).split("");
        const newOtp = [...otp];
        digits.forEach((digit, i) => {
          if (i < 4) newOtp[i] = digit;
        });
        setOtp(newOtp);
        inputRefs[Math.min(digits.length, 3)].current?.focus();
      });
    }
  };

  const handleVerify = async () => {
    const otpCode = otp.join("");
    
    if (otpCode.length !== 4) {
      toast.error("Please enter complete 4-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const res = await dispatch(verifyOTP({ email, otp: otpCode })).unwrap();
      
      if (res.status === "success") {
        toast.success("Email verified successfully!");
        onVerified();
        onClose();
      } else if (res.status === "invalid") {
        toast.error(res.message || "Invalid or expired OTP");
        setOtp(["", "", "", ""]);
        inputRefs[0].current?.focus();
      } else {
        toast.error(res.message || "Verification failed");
      }
    } catch (error) {
      toast.error(error.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;
    
    setLoading(true);
    try {
      const res = await dispatch(resendOTP(email)).unwrap();
      
      if (res.status === "success") {
        toast.success("OTP resent successfully!");
        setOtp(["", "", "", ""]);
        setTimeLeft(600);
        setCanResend(false);
        setResendTimer(60);
        inputRefs[0].current?.focus();
      } else {
        toast.error(res.message || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error(error.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="otp-overlay" onClick={onClose}>
        <motion.div
          className="otp-modal"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, ease: [0.22, 0.68, 0, 1.2] }}
        >
          {/* Header */}
          <div className="otp-header">
            <div className="otp-icon">
              <i className="bx bx-envelope"></i>
            </div>
            <h2 className="otp-title">Verify Your Email</h2>
            <p className="otp-subtitle">
              We've sent a 4-digit code to<br />
              <strong>{email}</strong>
            </p>
          </div>

          {/* OTP Inputs */}
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={inputRefs[index]}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="otp-input"
                disabled={loading}
              />
            ))}
          </div>

          {/* Timer */}
          <div className="otp-timer">
            <i className="bx bx-time-five"></i>
            <span>Code expires in {formatTime(timeLeft)}</span>
          </div>

          {/* Verify Button */}
          <button
            className="otp-verify-btn"
            onClick={handleVerify}
            disabled={loading || otp.join("").length !== 4}
          >
            {loading ? (
              <>
                <i className="bx bx-loader-alt bx-spin"></i> Verifying...
              </>
            ) : (
              <>
                <i className="bx bx-check-circle"></i> Verify Email
              </>
            )}
          </button>

          {/* Resend */}
          <div className="otp-resend">
            <span>Didn't receive the code?</span>
            <button
              className="otp-resend-btn"
              onClick={handleResend}
              disabled={!canResend || loading}
            >
              {canResend ? "Resend OTP" : `Resend in ${resendTimer}s`}
            </button>
          </div>

          {/* Close Button */}
          <button className="otp-close" onClick={onClose}>
            <i className="bx bx-x"></i>
          </button>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default OTPModal;
